import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CalendarDays, CheckCircle2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VinylWrapQuotePage() {
  const [params]    = useSearchParams();
  const navigate    = useNavigate();
  const token       = params.get('token');

  const [quote,   setQuote]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!token) { setError('Invalid link — no quote token found.'); setLoading(false); return; }
    axios.get(`${BACKEND_URL}/api/vinyl-wrap-quotes/preview/${token}`)
      .then(res => setQuote(res.data))
      .catch(() => setError('Quote not found or the link may have expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
      <div className="text-center max-w-sm">
        <AlertCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
        <h1 className="text-white font-bold text-xl mb-2">Quote Not Found</h1>
        <p className="text-zinc-500 text-sm mb-6">{error}</p>
        <button onClick={() => navigate('/wrap-designer')}
          className="px-6 py-3 bg-amber-500 text-black font-bold rounded-xl hover:bg-amber-400 transition-colors">
          Start a New Request
        </button>
      </div>
    </div>
  );

  const hasPricing = quote.admin_price != null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="max-w-lg mx-auto px-4 pt-12 pb-24">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-amber-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7 text-amber-400" />
          </div>
          <h1 className="text-2xl font-black text-white">Your Vinyl Wrap Quote</h1>
          <p className="text-zinc-500 text-sm mt-1">PDX Window Tint · Portland, OR</p>
        </div>

        {/* Quote Details Card */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden mb-4">
          <div className="px-5 py-4 border-b border-zinc-800">
            <p className="text-zinc-500 text-xs uppercase tracking-wider mb-0.5">Prepared for</p>
            <p className="text-white font-bold text-base">{quote.name}</p>
          </div>
          <div className="px-5 py-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Color</span>
              <span className="text-white font-semibold">{quote.color}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Finish</span>
              <span className="text-white font-semibold">{quote.finish}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Coverage</span>
              <span className="text-white font-semibold">{quote.coverage}</span>
            </div>
            {quote.message && (
              <div className="text-sm pt-1 border-t border-zinc-800">
                <span className="text-zinc-500 block mb-0.5">Notes</span>
                <span className="text-zinc-400 text-xs italic">"{quote.message}"</span>
              </div>
            )}
          </div>
        </div>

        {/* Pricing Card */}
        {hasPricing ? (
          <div className="bg-zinc-900 border border-amber-500/30 rounded-2xl px-5 py-6 mb-5 text-center">
            <p className="text-zinc-500 text-xs uppercase tracking-wider mb-2">Your Custom Price</p>
            <p className="text-amber-400 font-black text-5xl mb-1">${Number(quote.admin_price).toLocaleString()}</p>
            <p className="text-zinc-600 text-xs">Includes material, installation &amp; warranty</p>
          </div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl px-5 py-6 mb-5 text-center">
            <p className="text-zinc-500 text-sm">Your personalized price is being prepared.</p>
            <p className="text-zinc-600 text-xs mt-1">We'll email you as soon as it's ready.</p>
          </div>
        )}

        {/* CTA */}
        {hasPricing && (
          <button
            data-testid="wrap-quote-book-btn"
            onClick={() => navigate(`/?openBooking=Vinyl+Wrap&price=${quote.admin_price}&color=${encodeURIComponent(quote.color)}&finish=${encodeURIComponent(quote.finish)}`)}
            className="w-full py-4 bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-black font-black text-base rounded-2xl flex items-center justify-center gap-2.5 transition-all mb-3"
          >
            <CalendarDays className="w-5 h-5" />
            Book My Appointment · ${Number(quote.admin_price).toLocaleString()}
          </button>
        )}

        <button onClick={() => navigate('/wrap-designer')}
          className="w-full py-3.5 border border-zinc-800 text-zinc-400 hover:text-white text-sm font-semibold rounded-2xl transition-colors">
          Back to Wrap Designer
        </button>

        <p className="text-zinc-700 text-xs text-center mt-5 px-4">
          Questions? Call or text us at (503) 123-4567 or reply to the quote email.
        </p>
      </div>
    </div>
  );
}
